import { toast } from 'sonner';
import { useCallback } from 'react';
import { ONBOARDING_LAST_STEP, type OnboardingFlow } from '@/hooks/useOnboardingFlow';
import { analyticsClient } from '@/lib';
import { useOnboardingStepSchemas, validateOnboardingStep } from '@/lib/validators';
import { useOnboardingStore } from '@/stores/onboardingStore';

/** Persist store-backed fields and the next step to Firestore, then advance the wizard. */
export function useSaveAndNext(flow: OnboardingFlow) {
  const stepSchemas = useOnboardingStepSchemas();
  const saveCurrentData = useOnboardingStore(state => state.saveCurrentData);
  const setOnboardingStep = useOnboardingStore(state => state.setOnboardingStep);
  const setError = useOnboardingStore(state => state.setError);
  const getState = useOnboardingStore.getState;

  return useCallback(async () => {
    const validation = validateOnboardingStep(flow.step, getState(), stepSchemas);
    if (!validation.isValid) {
      setError(validation.error);
      toast.error(validation.error);
      return;
    }

    try {
      setError(null);
      setOnboardingStep(Math.min(flow.step + 1, ONBOARDING_LAST_STEP));
      await saveCurrentData();
      analyticsClient.capture('onboarding_step_completed', { step: flow.step });
      flow.next();
    } catch {
      // Store sets error state; stay on the current step.
    }
  }, [flow, getState, saveCurrentData, setError, setOnboardingStep, stepSchemas]);
}
